import * as React from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Route, Routes } from "react-router-dom"

import { NAV } from "@/components/layout/nav"
import { Shell } from "@/components/layout/shell"
import { Skeleton } from "@/components/ui/skeleton"
import { TooltipProvider } from "@/components/ui/tooltip"
import { AuthProvider, RequireAuth } from "@/lib/auth"
import { BlacklistTab } from "@/pages/lists/blacklist"
import { EmptyListTab } from "@/pages/lists/empty-tab"
import { ListsLayout } from "@/pages/lists/layout"
import { LoginPage } from "@/pages/login"
import { CustomRulesTab } from "@/pages/scoring/custom-rules"
import { DashboardTab } from "@/pages/scoring/dashboard"
import { DefaultRulesTab } from "@/pages/scoring/default-rules"
import { ScoringLayout } from "@/pages/scoring/layout"
import { LiveTab } from "@/pages/scoring/live"
import { LocalRulesTab } from "@/pages/scoring/local-rules"
import { ReplayTab } from "@/pages/scoring/replay"
import { RuleCardPage } from "@/pages/scoring/rule-card"
import { AuditPage } from "@/pages/settings/audit"
import { PeoplePage } from "@/pages/settings/people"
import { TimeoutsPage } from "@/pages/settings/timeouts"
import { NotFoundPage, StubPage } from "@/pages/stub"
import { TransactionCardPage } from "@/pages/transactions/card"
import { ImportPage } from "@/pages/transactions/import"
import { TransactionsListPage } from "@/pages/transactions/list"

import "./index.css"

/**
 * Маршруты Scoring Engine (`/scoring`, `/scoring/default`, `/scoring/custom`)
 * и списка транзакций (`/transactions`) — часть договорённости с приёмкой: по
 * ним пойдёт Visual QA. Переименовывать их нельзя, иначе прогон QA упрётся в
 * 404 вместо страницы.
 *
 * Карточка транзакции (`/transactions/:id`) стоит рядом со списком, а не внутри
 * него: соседняя колонка карточки ходит по той же выборке, и общий адрес держит
 * фильтры в URL, откуда их читают обе страницы.
 *
 * Разделы-заглушки собираются из того же `NAV`, что рисует меню: добавить пункт
 * в меню и забыть про маршрут (пункт есть, клик — в 404) так невозможно.
 *
 * Всё, кроме `/login`, стоит за `RequireAuth` (AMEND-8 §3 шаг 7) — включая
 * заглушки и «страницу не найдено». Заслон стоит **над** оболочкой, а не внутри
 * страниц: иначе невошедший всё равно увидел бы меню с названиями разделов и
 * шапку, то есть структуру чужой консоли.
 */
// Дашборд — отдельным куском: графики и контуры карты нужны только ему.
const DashboardPage = React.lazy(() =>
  import("@/pages/dashboard").then((module) => ({ default: module.DashboardPage })),
)

const root = document.getElementById("root")
if (!root) throw new Error("#root not found in index.html")

createRoot(root).render(
  <React.StrictMode>
    <TooltipProvider delayDuration={200} skipDelayDuration={0}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              element={
                <RequireAuth>
                  <Shell />
                </RequireAuth>
              }
            >
              {NAV.filter((item) => !item.live).map((item) => (
                <Route key={item.path} path={item.path} element={<StubPage title={item.label} />} />
              ))}
              <Route
                path="/"
                element={
                  <React.Suspense fallback={<Skeleton className="m-5 h-[360px]" />}>
                    <DashboardPage />
                  </React.Suspense>
                }
              />
              <Route path="/transactions" element={<TransactionsListPage />} />
              {/* Заливка (AMEND-16 §3 шаг 3) — вкладка раздела, а не пункт меню:
                  меню повторяет консоль SEON, где заливки нет вовсе. Статический
                  сегмент побеждает `:id` по ранжированию маршрутов, а не по
                  порядку строк, так что карточку он не перехватывает. */}
              <Route path="/transactions/import" element={<ImportPage />} />
              <Route path="/transactions/:id" element={<TransactionCardPage />} />
              <Route path="/settings" element={<PeoplePage />} />
              <Route path="/settings/audit" element={<AuditPage />} />
              <Route path="/settings/timeouts" element={<TimeoutsPage />} />
              {/* Вкладки-заглушки Lists (AMEND-12 §7) стоят **внутри** раздела,
                  а не среди общих заглушек `NAV`: у них своя полоса вкладок, и
                  без неё человек, открывший Whitelist, терял бы дорогу назад к
                  живому Blacklist. */}
              <Route path="/lists" element={<ListsLayout />}>
                <Route index element={<BlacklistTab />} />
                <Route
                  path="whitelist"
                  element={<EmptyListTab title="Whitelist" kind="whitelist" />}
                />
                <Route
                  path="custom"
                  element={<EmptyListTab title="Custom lists" kind="custom list" />}
                />
              </Route>
              <Route path="/scoring" element={<ScoringLayout />}>
                <Route index element={<DashboardTab />} />
                <Route path="default" element={<DefaultRulesTab />} />
                <Route path="custom" element={<CustomRulesTab />} />
                <Route path="local" element={<LocalRulesTab />} />
                <Route path="replay" element={<ReplayTab />} />
                <Route path="live" element={<LiveTab />} />
                <Route path="rules/:id" element={<RuleCardPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </React.StrictMode>,
)
